/**
 * Countries that use a comma as the decimal separator (e.g. 100,00 instead of 100.00).
 * Country names match the labels from `react-select-country-list` as stored in
 * `CurrentCompanyResponse.country`.
 */
export const COMMA_DECIMAL_COUNTRIES = new Set([
    'Albania',
    'Andorra',
    'Argentina',
    'Austria',
    'Belgium',
    'Bolivia',
    'Bosnia and Herzegovina',
    'Brazil',
    'Bulgaria',
    'Chile',
    'Colombia',
    'Croatia',
    'Czech Republic',
    'Denmark',
    'Estonia',
    'Faroe Islands',
    'Finland',
    'France',
    'Germany',
    'Greece',
    'Greenland',
    'Hungary',
    'Iceland',
    'Indonesia',
    'Italy',
    'Kosovo',
    'Latvia',
    'Lithuania',
    'Luxembourg',
    'Macedonia',
    'Moldova',
    'Montenegro',
    'Netherlands',
    'Norway',
    'Paraguay',
    'Peru',
    'Poland',
    'Portugal',
    'Romania',
    'Russia',
    'Serbia',
    'Slovakia',
    'Slovenia',
    'Spain',
    'Sweden',
    'Turkey',
    'Ukraine',
    'Uruguay',
    'Venezuela'
]);

/**
 * Returns the decimal separator for the given country name.
 * Defaults to '.' when country is unknown or not in the comma-decimal set.
 */
export const getDecimalSeparator = (country?: string | null): ',' | '.' => {
    if (!country) return '.';
    return COMMA_DECIMAL_COUNTRIES.has(country) ? ',' : '.';
};

/**
 * Parses a locale-formatted number string into a JS number.
 * The input may use ',' or '.' as decimal separator.
 * Always returns a native JS number so JSON serialization uses dot decimal.
 *
 * Examples:
 *   parseLocaleNumber("1.234,56", ',') → 1234.56
 *   parseLocaleNumber("1,234.56", '.') → 1234.56
 *   parseLocaleNumber("100,00",   ',') → 100
 *   parseLocaleNumber("100.00",   '.') → 100
 *   parseLocaleNumber("",         '.') → null
 *   parseLocaleNumber("abc",      '.') → null
 */
export const parseLocaleNumber = (
    value: string,
    decimalSeparator: ',' | '.'
): number | null => {
    if (value === '' || value === null || value === undefined) return null;

    let normalized: string;

    if (decimalSeparator === ',') {
        // Thousand separator is '.' → remove, then replace ',' decimal with '.'
        normalized = value.replace(/\./g, '').replace(',', '.');
    } else {
        // Thousand separator is ',' → remove
        normalized = value.replace(/,/g, '');
    }

    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? null : parsed;
};

/**
 * Detects if the user likely typed the wrong decimal separator.
 *
 * For comma-decimal locales (e.g. Denmark), "10.5" or "10.50" looks like the
 * user meant a decimal — but dots are stripped as thousand separators,
 * silently producing the wrong value 105 instead of 10.5.
 *
 * For dot-decimal locales, the same applies to commas.
 *
 * Thousand separators use 3-digit groups (e.g. "1.000" in comma-mode),
 * so we only flag when the "wrong" separator is followed by fewer than 3 digits.
 */
export const hasWrongDecimalSeparator = (
    value: string,
    decimalSeparator: ',' | '.'
): boolean => {
    if (!value) return false;
    const wrongSep = decimalSeparator === ',' ? '\\.' : ',';
    // Wrong separator followed by 1 or 2 trailing digits = likely a decimal mistake
    return new RegExp(`${wrongSep}\\d{1,2}$`).test(value);
};

/**
 * Whether a whole number uses the thousands separator anywhere other than
 * between proper 3-digit groups: "3000.0", "30.00" or "3000.000" in a
 * comma-decimal locale. "1.000" and "10.000.000" are valid.
 */
export const hasInvalidThousandsGrouping = (
    value: string,
    decimalSeparator: ',' | '.'
): boolean => {
    const thousands = decimalSeparator === ',' ? '.' : ',';
    const digits = value.trim().replace(/^-/, '');
    if (!digits.includes(thousands) || !/^[\d.,]+$/.test(digits)) return false;
    return !new RegExp(`^\\d{1,3}(\\${thousands}\\d{3})+$`).test(digits);
};

/**
 * Formats a number for display inside a numeric input field using the
 * locale decimal separator.
 *
 * Trailing-zero decimals are omitted: 22.00 → "22", 22.50 → "22,50".
 *
 * Examples:
 *   formatNumberForInput(1234.56, ',', 2) → "1234,56"
 *   formatNumberForInput(1234.56, '.', 2) → "1234.56"
 *   formatNumberForInput(100,     ',', 2) → "100"
 *   formatNumberForInput(22,      ',', 2) → "22"
 *   formatNumberForInput(22.5,    ',', 2) → "22,50"
 *   formatNumberForInput(100,     ',', 0) → "100"
 */
export const formatNumberForInput = (
    value: number,
    decimalSeparator: ',' | '.',
    digits: number = 2
): string => {
    const fixed = value.toFixed(digits);
    const [intPart, decPart] = fixed.split('.');

    // If there are no decimal digits, or all decimal digits are zero, show integer only
    if (!decPart || /^0+$/.test(decPart)) {
        return intPart;
    }

    if (decimalSeparator === ',') {
        return `${intPart},${decPart}`;
    }
    return fixed;
};
